/**
 * ProjectPhasesTab - Gestionarea etapelor din proiecte
 * Cu Gantt chart detaliat pentru fiecare proiect
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { 
  Layers, Plus, Trash2, Edit, ArrowLeft, ChevronLeft, ChevronRight,
  Calendar, Users, Loader2, Building2, CheckCircle, Clock, AlertTriangle,
  FileText
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Culori pentru etape
const PHASE_COLORS = [
  "#10B981", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6", 
  "#EC4899", "#06B6D4", "#84CC16", "#F97316", "#6366F1"
];

// Helper pentru formatare numere
const formatNumber = (num) => {
  if (num === undefined || num === null || isNaN(num)) return "0,00";
  return new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num);
};

export default function ProjectPhasesTab({ 
  site, 
  companyId, 
  subcontractors = [],
  users = [],
  devize = [],
  onBack 
}) {
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPhase, setShowAddPhase] = useState(false);
  const [editingPhase, setEditingPhase] = useState(null);
  const [summary, setSummary] = useState(null);
  
  // Timeline state
  const [timelineStart, setTimelineStart] = useState(null);
  const [daysToShow, setDaysToShow] = useState(60);
  
  // Formular etapă
  const [phaseForm, setPhaseForm] = useState({
    name: "",
    description: "",
    start_date: "",
    end_date: "",
    progress_percent: 0,
    total_value: 0,
    expenses: 0,
    assigned_team: [],
    assigned_subcontractors: [],
    color: PHASE_COLORS[0],
    deviz_id: ""
  });

  // Fetch phases
  const fetchPhases = useCallback(async () => {
    if (!site?.id || !companyId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/sites/${site.id}/phases/summary?company_id=${companyId}`);
      setPhases(response.data.phases || []);
      setSummary(response.data.summary);
      
      // Setează timeline-ul
      if (site.start_date) {
        setTimelineStart(new Date(site.start_date));
      } else if (response.data.phases?.length > 0) {
        const minDate = response.data.phases.reduce((min, p) => {
          const d = new Date(p.start_date);
          return d < min ? d : min;
        }, new Date(response.data.phases[0].start_date));
        setTimelineStart(minDate);
      } else {
        setTimelineStart(new Date());
      }
    } catch (error) {
      console.error("Error fetching phases:", error);
      toast.error("Eroare la încărcare etape");
    } finally {
      setLoading(false);
    }
  }, [site?.id, companyId]);

  useEffect(() => {
    fetchPhases();
  }, [fetchPhases]);

  // Reset form
  const resetForm = () => {
    setPhaseForm({
      name: "",
      description: "",
      start_date: site?.start_date || "",
      end_date: site?.end_date || "",
      progress_percent: 0,
      total_value: 0,
      expenses: 0,
      assigned_team: [],
      assigned_subcontractors: [],
      color: PHASE_COLORS[phases.length % PHASE_COLORS.length],
      deviz_id: ""
    });
  };

  // Adaugă etapă
  const handleAddPhase = async () => {
    if (!phaseForm.name || !phaseForm.start_date || !phaseForm.end_date) {
      toast.error("Completați câmpurile obligatorii");
      return;
    }
    
    try {
      await axios.post(`${API}/sites/${site.id}/phases?company_id=${companyId}`, {
        site_id: site.id,
        ...phaseForm
      });
      toast.success("Etapă adăugată!");
      setShowAddPhase(false);
      resetForm();
      fetchPhases();
    } catch (error) {
      toast.error("Eroare la adăugare etapă");
    }
  };

  // Editează etapă
  const handleEditPhase = async () => {
    if (!editingPhase) return;
    
    try {
      await axios.put(`${API}/phases/${editingPhase.id}?company_id=${companyId}`, phaseForm);
      toast.success("Etapă actualizată!");
      setEditingPhase(null);
      resetForm();
      fetchPhases();
    } catch (error) {
      toast.error("Eroare la actualizare");
    }
  };

  // Șterge etapă
  const handleDeletePhase = async (phaseId) => {
    if (!window.confirm("Sigur doriți să ștergeți această etapă?")) return;
    
    try {
      await axios.delete(`${API}/phases/${phaseId}?company_id=${companyId}`);
      toast.success("Etapă ștearsă!");
      fetchPhases();
    } catch (error) {
      toast.error("Eroare la ștergere");
    }
  };

  // Quick update progress
  const handleQuickUpdate = async (phaseId, field, value) => {
    try {
      await axios.put(`${API}/phases/${phaseId}?company_id=${companyId}`, { [field]: value });
      fetchPhases();
    } catch (error) {
      toast.error("Eroare la actualizare");
    }
  };

  // Timeline helpers
  const getDaysBetween = (start, end) => {
    const s = new Date(start);
    const e = new Date(end);
    return Math.ceil((e - s) / (1000 * 60 * 60 * 24));
  };

  const getPositionPercent = (date) => {
    if (!timelineStart) return 0;
    const d = new Date(date);
    const days = getDaysBetween(timelineStart, d);
    return (days / daysToShow) * 100;
  };

  const getWidthPercent = (startDate, endDate) => {
    const days = getDaysBetween(startDate, endDate) + 1;
    return (days / daysToShow) * 100;
  };

  // Generează zilele pentru timeline
  const generateTimelineDays = () => {
    if (!timelineStart) return [];
    const days = [];
    for (let i = 0; i < daysToShow; i++) {
      const date = new Date(timelineStart);
      date.setDate(date.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const timelineDays = generateTimelineDays();

  // Navigare timeline
  const navigatePrev = () => {
    const newStart = new Date(timelineStart);
    newStart.setDate(newStart.getDate() - 30);
    setTimelineStart(newStart);
  };

  const navigateNext = () => {
    const newStart = new Date(timelineStart);
    newStart.setDate(newStart.getDate() + 30);
    setTimelineStart(newStart);
  };

  if (!site) {
    return <div>Selectați un proiect</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <CardTitle className="flex items-center gap-2 text-xl sm:text-2xl font-bold" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  <Layers className="w-6 h-6 text-blue-600" />
                  {site.name}
                </CardTitle>
                <CardDescription className="flex items-center gap-4 mt-1">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {site.start_date} - {site.end_date}
                  </span>
                  <span className="flex items-center gap-1">
                    <Layers className="w-4 h-4" />
                    {phases.length} etape
                  </span>
                </CardDescription>
              </div>
            </div>
            <Button onClick={() => { resetForm(); setShowAddPhase(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="add-phase-btn">
              <Plus className="w-4 h-4 mr-2" />
              Adaugă Etapă
            </Button>
          </div>
        </CardHeader>

        {/* Summar */}
        {summary && (
          <CardContent className="pt-0">
            <div className="grid grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm text-slate-500">Total Etape</p>
                <p className="text-2xl font-bold text-blue-600">{summary.phase_count}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Valoare Etape</p>
                <p className="text-2xl font-bold text-green-600">{formatNumber(summary.total_phases_value)} RON</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Cheltuieli Etape</p>
                <p className="text-2xl font-bold text-amber-600">{formatNumber(summary.total_phases_expenses)} RON</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Progres Mediu</p>
                <p className="text-2xl font-bold text-purple-600">{summary.average_progress}%</p>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Gantt Chart pentru Etape */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Grafic Gantt - Etape</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={navigatePrev}>
                <ChevronLeft className="w-4 h-4" />
                Luna anterioară
              </Button>
              <Button variant="outline" size="sm" onClick={navigateNext}>
                Luna următoare
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : phases.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg">
              <Layers className="w-12 h-12 mx-auto text-slate-400 mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">Nu există etape</h3>
              <p className="text-slate-500 mb-4">Adăugați prima etapă pentru a vedea graficul</p>
              <Button onClick={() => { resetForm(); setShowAddPhase(true); }} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />
                Adaugă Etapă
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              {/* Timeline Header */}
              <div className="min-w-[1200px]">
                <div className="flex border-b">
                  <div className="w-64 shrink-0 p-2 font-medium bg-slate-50">Etapă</div>
                  <div className="flex-1 flex">
                    {timelineDays.map((day) => (
                      <div 
                        key={day.toISOString().split('T')[0]}
                        className={`flex-1 text-center text-xs p-1 border-l ${
                          day.getDay() === 0 || day.getDay() === 6 ? 'bg-slate-100' : ''
                        } ${day.toDateString() === new Date().toDateString() ? 'bg-red-100' : ''}`}
                      >
                        {day.getDate() === 1 && (
                          <div className="font-bold text-blue-600">
                            {day.toLocaleDateString('ro-RO', { month: 'short' })}
                          </div>
                        )}
                        <div className={day.toDateString() === new Date().toDateString() ? 'text-red-600 font-bold' : ''}>
                          {day.getDate()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Phase Bars */}
                {phases.map((phase, idx) => (
                  <PhaseRow
                    key={phase.id}
                    phase={phase}
                    index={idx}
                    timelineStart={timelineStart}
                    daysToShow={daysToShow}
                    getPositionPercent={getPositionPercent}
                    getWidthPercent={getWidthPercent}
                    onEdit={(p) => {
                      setPhaseForm({
                        name: p.name,
                        description: p.description || "",
                        start_date: p.start_date,
                        end_date: p.end_date,
                        progress_percent: p.progress_percent || 0,
                        total_value: p.total_value || 0,
                        expenses: p.expenses || 0,
                        assigned_team: p.assigned_team || [],
                        assigned_subcontractors: p.assigned_subcontractors || [],
                        color: p.color || PHASE_COLORS[idx % PHASE_COLORS.length],
                        deviz_id: p.deviz_id || ""
                      });
                      setEditingPhase(p);
                    }}
                    onDelete={handleDeletePhase}
                    onQuickUpdate={handleQuickUpdate}
                    subcontractors={subcontractors}
                  />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog Adaugă/Editează Etapă */}
      <Dialog open={showAddPhase || !!editingPhase} onOpenChange={(open) => {
        if (!open) {
          setShowAddPhase(false);
          setEditingPhase(null);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-blue-600" />
              {editingPhase ? "Editează Etapă" : "Adaugă Etapă Nouă"}
            </DialogTitle>
            <DialogDescription>
              Completați detaliile etapei din proiectul {site.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Denumire Etapă *</Label>
                <Input 
                  value={phaseForm.name}
                  onChange={(e) => setPhaseForm({...phaseForm, name: e.target.value})}
                  placeholder="Ex: Fundație, Structură, Finisaje..."
                />
              </div>
              <div>
                <Label>Data Început *</Label>
                <Input 
                  type="date"
                  value={phaseForm.start_date}
                  onChange={(e) => setPhaseForm({...phaseForm, start_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Data Sfârșit *</Label>
                <Input 
                  type="date"
                  value={phaseForm.end_date}
                  onChange={(e) => setPhaseForm({...phaseForm, end_date: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Descriere</Label>
              <Input 
                value={phaseForm.description}
                onChange={(e) => setPhaseForm({...phaseForm, description: e.target.value})}
                placeholder="Descriere activități..."
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Valoare Buget (RON)</Label>
                <Input 
                  type="number"
                  value={phaseForm.total_value}
                  onChange={(e) => setPhaseForm({...phaseForm, total_value: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Cheltuieli (RON)</Label>
                <Input 
                  type="number"
                  value={phaseForm.expenses}
                  onChange={(e) => setPhaseForm({...phaseForm, expenses: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Progres (%)</Label>
                <div className="flex items-center gap-2">
                  <Slider 
                    value={[phaseForm.progress_percent]}
                    onValueChange={(v) => setPhaseForm({...phaseForm, progress_percent: v[0]})}
                    max={100}
                    step={5}
                    className="flex-1"
                  />
                  <span className="w-12 text-right font-medium">{phaseForm.progress_percent}%</span>
                </div>
              </div>
            </div>

            <div>
              <Label>Subcontractori Executanți</Label>
              <Select 
                value={phaseForm.assigned_subcontractors[0] || "none"}
                onValueChange={(v) => setPhaseForm({...phaseForm, assigned_subcontractors: v && v !== "none" ? [v] : []})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selectează subcontractor..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Fără subcontractor</SelectItem>
                  {subcontractors.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Deviz Asociat</Label>
              <Select 
                value={phaseForm.deviz_id || "none"}
                onValueChange={(v) => setPhaseForm({...phaseForm, deviz_id: v === "none" ? "" : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selectează deviz..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Fără deviz</SelectItem>
                  {devize.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.numar_deviz || d.numarDeviz} - {d.beneficiar || "Fără beneficiar"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Culoare</Label>
              <div className="flex gap-2 mt-1">
                {PHASE_COLORS.map(color => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full border-2 ${phaseForm.color === color ? 'border-slate-800 scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setPhaseForm({...phaseForm, color})}
                  />
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => { setShowAddPhase(false); setEditingPhase(null); resetForm(); }}>
                Anulează
              </Button>
              <Button 
                onClick={editingPhase ? handleEditPhase : handleAddPhase} 
                className="bg-blue-600 hover:bg-blue-700"
              >
                {editingPhase ? "Salvează Modificări" : "Adaugă Etapă"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Component pentru rândul unei etape în Gantt
function PhaseRow({ 
  phase, 
  index, 
  timelineStart,
  daysToShow,
  getPositionPercent, 
  getWidthPercent, 
  onEdit, 
  onDelete,
  onQuickUpdate,
  subcontractors 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [localProgress, setLocalProgress] = useState(phase.progress_percent || 0);
  const [localExpenses, setLocalExpenses] = useState(phase.expenses || 0);

  const left = getPositionPercent(phase.start_date);
  const width = getWidthPercent(phase.start_date, phase.end_date);
  
  // Calculează progresul temporal
  const today = new Date();
  const start = new Date(phase.start_date);
  const end = new Date(phase.end_date);
  const totalDays = (end - start) / (1000 * 60 * 60 * 24);
  const daysPassed = Math.max(0, (today - start) / (1000 * 60 * 60 * 24));
  const timeProgress = Math.min(100, (daysPassed / totalDays) * 100);
  
  // Status
  const isDelayed = phase.progress_percent < timeProgress - 10;
  const isAhead = phase.progress_percent > timeProgress + 10;

  const subcontractorName = phase.assigned_subcontractors?.[0] 
    ? subcontractors.find(s => s.id === phase.assigned_subcontractors[0])?.name 
    : null;

  return (
    <div className="flex border-b hover:bg-slate-50">
      {/* Info Etapă */}
      <div className="w-64 shrink-0 p-2 border-r">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate" title={phase.name}>{phase.name}</p>
            <p className="text-xs text-slate-500">
              {phase.start_date} - {phase.end_date}
            </p>
            {subcontractorName && (
              <Badge variant="outline" className="text-xs mt-1">
                <Building2 className="w-3 h-3 mr-1" />
                {subcontractorName}
              </Badge>
            )}
          </div>
          <div className="flex gap-1 ml-2">
            <Button variant="ghost" size="sm" onClick={() => onEdit(phase)} className="h-7 w-7 p-0">
              <Edit className="w-3 h-3" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDelete(phase.id)} className="h-7 w-7 p-0 text-red-500">
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>

      {/* Bar Gantt */}
      <div className="flex-1 relative h-16 py-2">
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <div 
              className="absolute h-10 rounded cursor-pointer hover:opacity-90 transition-opacity shadow-sm"
              style={{
                left: `${Math.max(0, left)}%`,
                width: `${Math.min(width, 100 - Math.max(0, left))}%`,
                backgroundColor: phase.color || PHASE_COLORS[index % PHASE_COLORS.length],
                top: '50%',
                transform: 'translateY(-50%)'
              }}
            >
              {/* Progress bar */}
              <div 
                className="absolute top-0 left-0 h-full rounded bg-black/20"
                style={{ width: `${phase.progress_percent}%` }}
              />
              
              {/* Label */}
              <div className="absolute inset-0 flex items-center justify-between px-2 text-white text-xs font-medium">
                <span className="truncate">{phase.name}</span>
                <span className="flex items-center gap-1">
                  {isDelayed && <AlertTriangle className="w-3 h-3 text-yellow-300" />}
                  {isAhead && <CheckCircle className="w-3 h-3 text-green-300" />}
                  {phase.progress_percent}%
                </span>
              </div>
            </div>
          </PopoverTrigger>
          
          <PopoverContent className="w-80 p-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold">{phase.name}</h4>
                {phase.deviz_id && (
                  <Badge variant="outline" className="text-green-600">
                    <FileText className="w-3 h-3 mr-1" />
                    Deviz
                  </Badge>
                )}
              </div>
              
              {/* Progress Slider */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Stadiu Fizic</span>
                  <span className="font-medium">{localProgress}%</span>
                </div>
                <Slider 
                  value={[localProgress]}
                  onValueChange={(v) => setLocalProgress(v[0])}
                  max={100}
                  step={5}
                />
                <div className="flex gap-1 mt-2">
                  {[0, 25, 50, 75, 100].map(v => (
                    <Button 
                      key={v} 
                      size="sm" 
                      variant={localProgress === v ? "default" : "outline"}
                      onClick={() => setLocalProgress(v)}
                      className="flex-1 h-6 text-xs"
                    >
                      {v}%
                    </Button>
                  ))}
                </div>
              </div>
              
              {/* Cheltuieli */}
              <div>
                <Label className="text-sm">Cheltuieli Reale (RON)</Label>
                <Input 
                  type="number"
                  value={localExpenses}
                  onChange={(e) => setLocalExpenses(parseFloat(e.target.value) || 0)}
                  className="mt-1"
                />
                {phase.total_value > 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    Buget: {formatNumber(phase.total_value)} RON 
                    ({Math.round((localExpenses / phase.total_value) * 100)}% utilizat)
                  </p>
                )}
              </div>
              
              {/* Salvare */}
              <Button 
                className="w-full"
                onClick={() => {
                  onQuickUpdate(phase.id, 'progress_percent', localProgress);
                  onQuickUpdate(phase.id, 'expenses', localExpenses);
                  setIsOpen(false);
                }}
              >
                Salvează
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
